import { useMemo, useState } from 'react';
import {
  useCancelContentRun,
  useContentBriefQuery,
  useContentDraftsQuery,
  useApproveContentDraft,
  useRevertContentDraft,
  useSaveContentDraft,
  useContentRunsQuery,
  useQueueContentRun,
  useRefreshSiteContent,
  useSiteContentQuery,
} from '../../api/queries';
import RefreshConfirm from './RefreshConfirm';
import DraftEditorDrawer from './DraftEditorDrawer';
import { toCsv, downloadCsv } from '../../lib/csv';
import { showToast } from '../../components/Toast';
import type { SiteContentPage } from '../../types';

type Props = { af: string };

/** Pages are renewed annually, so anything past a year is the working
 *  definition of "due" — not a crawl-freshness number. */
const DUE_AFTER_DAYS = 365;

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

/** Minutes since an ISO timestamp. */
function sinceMins(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
}

/** Approved long enough ago that nothing is plausibly working on it.
 *  A publish takes well under a minute once claimed, so past this the honest
 *  reading is that no publisher is running, not that it is slow. */
const STALE_AFTER_MINS = 5;
function stale(approvedAt: string): boolean {
  return sinceMins(approvedAt) >= STALE_AFTER_MINS;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function todayStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function exportCsv(af: string, pages: SiteContentPage[]): void {
  const headers = ['H1', 'Path', 'URL', 'Type', 'Last Modified', 'Last Refreshed', 'Status', 'Source'];
  downloadCsv(
    `on-site-content_${af}_${todayStamp()}.csv`,
    toCsv(headers, pages.map((p) => [
      p.h1 ?? '', p.path, p.url, p.pageType ?? '',
      p.siteModifiedAt ?? '', p.lastRefreshedAt ?? '',
      p.httpStatus ?? '', p.source,
    ])),
  );
}

export default function OnSiteContentSubTab({ af }: Props) {
  const q = useSiteContentQuery(af, true);
  const refresh = useRefreshSiteContent(af);
  const runs = useContentRunsQuery(af, true);
  const queueRun = useQueueContentRun(af);
  const cancelRun = useCancelContentRun(af);
  const brief = useContentBriefQuery(af);
  const [onlyDue, setOnlyDue] = useState(false);
  // URLs the human has ticked. Keyed by URL rather than index so filtering the
  // table (Never refreshed) cannot silently change what is selected.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const drafts = useContentDraftsQuery(af, true);
  const saveDraft = useSaveContentDraft(af);
  const revertDraft = useRevertContentDraft(af);
  const approveDraft = useApproveContentDraft(af);
  const [openDraftId, setOpenDraftId] = useState<number | null>(null);

  /** URL -> its latest draft, for the per-row "View and edit draft" action. */
  const draftByUrl = useMemo(() => {
    const m = new Map<string, (typeof list)[number]>();
    const list = drafts.data?.drafts ?? [];
    for (const d of list) if (d.url) m.set(d.url, d);
    return m;
  }, [drafts.data]);

  // Drafts the pipeline produced that could not be tied to a page in the
  // inventory. They are real work and must not be invisible just because the
  // match failed, so they get their own line above the table.
  // Pages approved but not yet published — what the publisher still owes.
  const publishing = useMemo(
    () => (drafts.data?.drafts ?? []).filter((d) => d.approvedAt && !d.publishedAt),
    [drafts.data],
  );
  const publishStalled = publishing.length > 0 && publishing.every((d) => stale(d.approvedAt!));

  const unmatchedDrafts = useMemo(
    () => (drafts.data?.drafts ?? []).filter((d) => !d.url),
    [drafts.data],
  );

  const openDraft = useMemo(
    () => (drafts.data?.drafts ?? []).find((d) => d.id === openDraftId) ?? null,
    [drafts.data, openDraftId],
  );
  // Which pages a pending confirm would refresh. null = dialog closed.
  const [pendingPages, setPendingPages] = useState<SiteContentPage[] | null>(null);

  const pages = useMemo(() => q.data?.pages ?? [], [q.data]);
  const wpFed = pages.some((p) => p.source === 'wordpress');

  const dueCount = useMemo(
    () =>
      pages.filter((p) => {
        const age = daysSince(p.lastRefreshedAt ?? p.siteModifiedAt);
        return p.lastRefreshedAt === null || (age !== null && age > DUE_AFTER_DAYS);
      }).length,
    [pages],
  );

  const visible = useMemo(
    () => (onlyDue ? pages.filter((p) => p.lastRefreshedAt === null) : pages),
    [pages, onlyDue],
  );

  const latestRun = runs.data?.runs?.[0] ?? null;
  const active =
    latestRun && (latestRun.status === 'queued' || latestRun.status === 'running')
      ? latestRun
      : null;
  // The last run that actually finished — what "last refreshed" should measure
  // against, not a queued one that has not done anything yet.
  const lastFinished =
    runs.data?.runs?.find((r) => r.status === 'succeeded') ?? null;

  function toggle(url: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(url) ? next.delete(url) : next.add(url);
      return next;
    });
  }

  /** Select-all applies to what is VISIBLE, which is what the header checkbox
   *  appears to promise when a filter is on. */
  const allVisibleSelected = visible.length > 0 && visible.every((p) => selected.has(p.url));
  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((p) => next.delete(p.url));
      else visible.forEach((p) => next.add(p.url));
      return next;
    });
  }

  async function confirmRun() {
    if (!pendingPages) return;
    try {
      const r = await queueRun.mutateAsync(
        // An empty list means "everything the inventory knows about", which is
        // what the server records; sending every URL would be the same job with
        // a worse audit trail.
        pendingPages.length === pages.length ? [] : pendingPages.map((p) => p.url),
      );
      showToast(`Queued — ${r.pageCount} page(s), est. $${r.estimatedCostUsd.toFixed(2)}`, {
        variant: 'success',
      });
      setPendingPages(null);
      setSelected(new Set());
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    }
  }

  async function runRefresh() {
    try {
      const fresh = await refresh.mutateAsync();
      // A crawl that reaches nothing still returns 200 with an errorMessage, so
      // reporting plain success here told people it had worked when it had not.
      if (fresh.urlCount === 0) {
        showToast(fresh.errorMessage ?? 'No pages found on this site', { variant: 'error' });
      } else {
        showToast(`Found ${fresh.urlCount} page${fresh.urlCount === 1 ? '' : 's'}`, {
          variant: 'success',
        });
      }
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    }
  }

  if (q.isLoading) {
    return (
      <div className="listings-loading">
        <div className="spinner" />
        Loading site content…
      </div>
    );
  }

  if (q.error) {
    return (
      <div className="tab-placeholder">
        <h3>Site Content Unavailable</h3>
        <p>{(q.error as Error).message}</p>
      </div>
    );
  }

  const syncedAt = q.data?.syncedAt ?? null;
  const busy = refresh.isPending;

  // Three states, not two. An attempt that found nothing used to render exactly
  // like never having attempted — same heading, same button, and the error
  // explaining WHY was dropped because this branch returns before it is shown.
  // Clicking the button then appeared to do nothing at all.
  if (!syncedAt) {
    const attempted = Boolean(q.data?.lastAttemptAt);
    const why = q.data?.errorMessage;
    return (
      <div className="tab-placeholder">
        <h3>{attempted ? 'Nothing came back' : 'No page inventory yet'}</h3>
        {attempted ? (
          <>
            <p>
              The last read of this site returned no pages
              {q.data?.lastAttemptAt ? ` (${fmtDate(q.data.lastAttemptAt)})` : ''}.
            </p>
            {why && <div className="schema-warning">{why}</div>}
            <p className="field-help">
              This is usually the site refusing us rather than a franchisee with no
              pages — the marketing server's IP is currently blocked at the origin,
              so a crawl only sees what happens to be cached. Pushing the inventory
              in from WordPress avoids it entirely.
            </p>
          </>
        ) : (
          <p>
            Read this franchisee's site to list every page, its H1, and when it last
            changed — the starting point for deciding what needs rewriting.
          </p>
        )}
        <button type="button" className="schema-refresh-btn" onClick={runRefresh} disabled={busy}>
          {busy ? 'Reading site…' : attempted ? '↻ Try again' : 'Build inventory'}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="schema-header">
        <div className="schema-meta">
          <span className="schema-count">
            {pages.length} page{pages.length === 1 ? '' : 's'}
          </span>
          <span className="schema-crawled">
            Last synced {fmtDate(syncedAt)}{wpFed ? ' from WordPress' : ''}
          </span>
        </div>
        <div className="schema-actions">
          {/* Two different verbs, deliberately: one re-reads what is on the
              site, the other spends money redrafting it. */}
          <button
            type="button"
            className="schema-refresh-btn"
            onClick={() =>
              setPendingPages(
                selected.size > 0 ? pages.filter((p) => selected.has(p.url)) : pages,
              )
            }
            disabled={busy || !!active || pages.length === 0}
            title={
              selected.size > 0
                ? 'Rewrite the selected pages'
                : 'Rewrite every page — tick individual rows to narrow it'
            }
          >
            {selected.size > 0
              ? `✦ Refresh ${selected.size} selected`
              : '✦ Refresh content'}
          </button>
          {/* A WordPress-fed inventory has the real H1s and post_modified dates;
              a crawl would overwrite them with worse data, so it isn't offered
              (the server refuses it too). Re-run the WordPress feed instead. */}
          {!wpFed && (
            <button type="button" className="schema-refresh-btn" onClick={runRefresh} disabled={busy}>
              {busy ? 'Reading site…' : '↻ Re-read site'}
            </button>
          )}
          <button
            type="button"
            className="export-csv-btn admin-only"
            onClick={() => exportCsv(af, pages)}
            disabled={pages.length === 0}
          >
            ⬇ Download CSV
          </button>
        </div>
      </div>

      {/* A queued or running job, so a long run never looks like nothing
          happened. Polls itself while in flight and then stops. */}
      {active && (
        <div className="run-strip">
          <span className={`run-dot is-${active.status}`} />
          <span>
            {active.status === 'queued'
              ? 'Queued — waiting for a worker to pick it up'
              : active.progress || 'Running…'}
          </span>
          <span className="run-spacer" />
          {active.estimatedCostUsd != null && <span>est. ${active.estimatedCostUsd.toFixed(2)}</span>}
          {active.status === 'queued' && (
            <button type="button" onClick={() => cancelRun.mutate(active.id)}>
              Cancel
            </button>
          )}
        </div>
      )}
      {/* Mirrors the content-run strip: a long operation that happens elsewhere
          should say so on screen rather than look like nothing happened. */}
      {publishing.length > 0 && (
        <div className={`run-strip${publishStalled ? ' is-stalled' : ''}`}>
          {publishStalled ? <span className="run-dot is-failed" /> : <span className="mini-spinner" />}
          <span>
            {publishStalled
              ? `${publishing.length} page${publishing.length === 1 ? '' : 's'} approved but not published — the publisher does not appear to be running`
              : `Publishing ${publishing.length} page${publishing.length === 1 ? '' : 's'} — ${publishing
                  .map((d) => d.city ?? 'page')
                  .join(', ')}`}
          </span>
        </div>
      )}

      {latestRun?.status === 'failed' && (
        <div className="schema-warning">
          Last refresh failed: {latestRun.error || 'no reason given'}
        </div>
      )}

      {q.data?.errorMessage && <div className="schema-warning">{q.data.errorMessage}</div>}

      {/* An empty table after a successful sync is ambiguous — it can mean a
          franchisee with no pages, or a site we could not read. Say which. */}
      {pages.length === 0 && !q.data?.errorMessage && (
        <div className="schema-warning">
          No pages found. If this franchisee definitely has a site, the read was most
          likely blocked rather than empty — check the site URL on the Profile tab.
        </div>
      )}

      {unmatchedDrafts.length > 0 && (
        <div className="run-strip">
          <span>
            {unmatchedDrafts.length} draft{unmatchedDrafts.length === 1 ? '' : 's'} could not be
            matched to a page in this list
          </span>
          <span className="run-spacer" />
          {unmatchedDrafts.map((d) => (
            <button key={d.id} type="button" onClick={() => setOpenDraftId(d.id)}>
              {d.city ?? `#${d.idx + 1}`}
            </button>
          ))}
        </div>
      )}

      {pages.length > 0 && (
        <div className="schema-summary">
          <button
            type="button"
            className={`schema-chip schema-chip-missing${onlyDue ? ' is-active' : ''}`}
            onClick={() => setOnlyDue((v) => !v)}
            title="Show only pages we have never regenerated"
          >
            Never refreshed <strong>{dueCount}</strong>
          </button>
        </div>
      )}

      <div className="calls-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: 34 }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  disabled={visible.length === 0}
                  aria-label="Select all pages shown"
                />
              </th>
              <th>H1</th>
              <th>Path</th>
              <th style={{ width: 120 }}>Type</th>
              <th style={{ width: 120 }}>Modified</th>
              <th style={{ width: 120 }}>Refreshed</th>
              <th style={{ width: 150 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr key={p.url} className={selected.has(p.url) ? 'is-selected' : undefined}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(p.url)}
                    onChange={() => toggle(p.url)}
                    aria-label={`Select ${p.path}`}
                  />
                </td>
                <td>{p.h1 ?? <span className="muted">—</span>}</td>
                <td><code>{p.path}</code></td>
                <td>{p.pageType ?? <span className="muted">—</span>}</td>
                <td
                  title={
                    p.source === 'sitemap'
                      ? "From the sitemap's lastmod, which moves on any edit to the page — treat as a hint"
                      : undefined
                  }
                >
                  {fmtDate(p.siteModifiedAt)}
                </td>
                <td>{fmtDate(p.lastRefreshedAt)}</td>
                <td>
                  <a href={p.url} target="_blank" rel="noopener noreferrer">Open ↗</a>
                  {' · '}
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => setPendingPages([p])}
                    disabled={!!active}
                    title="Redraft just this page"
                  >
                    Refresh
                  </button>
                  {draftByUrl.has(p.url) && (
                    <>
                      {' · '}
                      {(() => {
                        const d = draftByUrl.get(p.url)!;
                        // Publishing state belongs on the PAGE it describes, not
                        // on a button: a territory-wide publish then shows which
                        // pages are in flight, and "Published <date>" is a fact
                        // the row can keep rather than a spinner with nowhere to
                        // land.
                        if (d.publishedAt) {
                          return (
                            <span className="publish-state is-done" title={`Post ${d.publishedWpPostId ?? '?'}`}>
                              Published {fmtDate(d.publishedAt)}
                            </span>
                          );
                        }
                        if (d.approvedAt) {
                          // The dashboard can only report what the publisher told
                          // it. If nothing is listening, an optimistic spinner
                          // would imply progress that is not happening — so once
                          // it has been waiting a while, say so instead.
                          return stale(d.approvedAt) ? (
                            <span className="publish-state is-stale" title={`Approved ${new Date(d.approvedAt).toLocaleString()}`}>
                              ⚠ Waiting {sinceMins(d.approvedAt)}m — publisher hasn't picked this up
                            </span>
                          ) : (
                            <span className="publish-state is-working">
                              <span className="mini-spinner" /> Publishing…
                            </span>
                          );
                        }
                        return (
                          <button
                            type="button"
                            className="link-btn is-draft"
                            onClick={() => setOpenDraftId(d.id)}
                            title="Review and edit the drafted page"
                          >
                            {d.edited ? 'Edit draft ✎' : 'View draft'}
                          </button>
                        );
                      })()}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openDraft && (
        <DraftEditorDrawer
          draft={openDraft}
          saving={saveDraft.isPending || revertDraft.isPending || approveDraft.isPending}
          onSave={async (patch) => {
            await saveDraft.mutateAsync({ id: openDraft.id, ...patch });
          }}
          onRevert={async () => {
            await revertDraft.mutateAsync(openDraft.id);
          }}
          onApprove={async () => {
            await approveDraft.mutateAsync({ id: openDraft.id, approved: true });
          }}
          onClose={() => setOpenDraftId(null)}
        />
      )}

      {pendingPages && (
        <RefreshConfirm
          pages={pendingPages}
          lastRun={lastFinished}
          hasBrief={Boolean(brief.data?.brief && brief.data.brief.status !== 'draft')}
          busy={queueRun.isPending}
          onConfirm={confirmRun}
          onCancel={() => setPendingPages(null)}
        />
      )}
    </>
  );
}
